import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { CheckCircle2, Loader2, AlertTriangle, Calendar, Euro } from "lucide-react";

const cents = (v) => (Number(v || 0) / 100).toFixed(2) + " €";

export default function OnboardingConfirmationPage() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("polling");
  const [rid, setRid] = useState(null);
  const [installments, setInstallments] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/agency/onboarding/setup-status/${sessionId}`);
        const st = r.data;
        if (st.payment_status === "paid") {
          setStatus("paid");
          setRid(st.onboarding_request_id);
          return;
        }
        if (st.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch { /* retry */ }
      if (attempts < 12) setTimeout(poll, 2500);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  useEffect(() => {
    if (!rid) return;
    api.get(`/agency/onboarding/requests/${rid}/installments`)
      .then(r => setInstallments(r.data))
      .catch(() => {});
  }, [rid]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white py-10 px-4" data-testid="onboarding-confirmation">
      <div className="max-w-2xl mx-auto">
        <Card className="p-8 bg-slate-800 border-slate-700 text-white text-center">
          {status === "polling" && (
            <div className="space-y-4 py-6">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-sky-400" />
              <h2 className="text-xl font-bold">Vérification du paiement…</h2>
              <p className="text-sm text-slate-300">Cela prend quelques secondes — merci de patienter.</p>
            </div>
          )}
          {status === "paid" && (
            <div className="space-y-4 py-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Paiement accepté ✓</h2>
              <p className="text-slate-300">Votre première échéance a bien été réglée. Notre équipe prend le relais sous 24h.</p>
              {installments && (
                <div className="mt-6 text-left">
                  <h3 className="text-sm font-bold mb-2 text-slate-300">Calendrier des échéances</h3>
                  <div className="space-y-2" data-testid="setup-installments-list">
                    {installments.schedule.map(it => (
                      <div key={it.number} className={`flex items-center justify-between p-3 rounded-lg border ${it.status === "paid" ? "bg-emerald-900/30 border-emerald-700" : "bg-slate-900 border-slate-700"}`}>
                        <div className="flex items-center gap-3">
                          {it.status === "paid"
                            ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            : <Calendar className="w-5 h-5 text-slate-400" />
                          }
                          <div>
                            <div className="font-medium text-sm">Échéance {it.number}/{installments.setup_installments}</div>
                            <div className="text-xs text-slate-400">Due le {(it.due_at || "").slice(0, 10)}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">{cents(it.amount_cents)}</div>
                          <div className={`text-xs ${it.status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>
                            {it.status === "paid" ? "Payée" : "En attente"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button asChild className="mt-4"><Link to="/">Retour à l'accueil</Link></Button>
            </div>
          )}
          {(status === "expired" || status === "timeout" || status === "error") && (
            <div className="space-y-4 py-6">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-400" />
              <h2 className="text-xl font-bold">
                {status === "expired" ? "Session expirée" : status === "timeout" ? "Vérification trop longue" : "Session introuvable"}
              </h2>
              <p className="text-sm text-slate-300">
                {status === "expired"
                  ? "Le délai de paiement est dépassé. Relancez votre demande d'onboarding pour générer un nouveau lien."
                  : "Votre paiement est peut-être en cours — nous vous enverrons un email de confirmation dès réception."}
              </p>
              <Button asChild variant="outline" className="text-white border-slate-500"><Link to="/onboarding">Nouvelle demande</Link></Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
